import React, {createRef, ReactNode, RefObject} from "react";
import {HtmlViewerProperties} from "../html/HtmlViewer";
import AbstractViewer, {AbstractViewerProperties, AbstractViewerState} from "../AbstractViewer";
import {ViewerMode} from "../ViewerMode";
import MouseMode from "../settings/MouseMode";
import DomHelper from "../../../shared/helper/DomHelper";
import Exception from "../../../shared/exception/Exception";
import PortalDocument from "../../document/PortalDocument";
import MessageBox from "../../../shared/components/MessageBox";
import i18next from "i18next";
import MimeTypeFilter from "../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../document/mimetype/MimeTypes";
import {MessageType} from "../../../shared/log/MessageInterface";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import Icon from "../../../shared/components/Icon";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import TextContainer, {PortalHighlightResult} from "../../document/TextContainer";
import LengthUnit from "../../../shared/units/LengthUnit";
import {Dropdown} from "primereact/dropdown";
import HighlighterLanguage from "../../dialog/parameter/HighlighterLanguage";
import {SelectItem} from "primereact/selectitem";
import DocumentSettings from "../../document/DocumentSettings";
import portalConfiguration from "../../../shared/configuration/PortalConfiguration";
import ReactMarkdown from "react-markdown";

export interface TextViewerProperties extends AbstractViewerProperties {
	wrapText?: boolean;
	viewAsMarkdown?: boolean;
}

export interface TextViewerState extends AbstractViewerState {
	viewerData?: PortalHighlightResult;
	loading: boolean;
	error?: string;
	reportOpen: boolean;
	loadedFrom: number;
	loadedTo: number;
	loadingOffset: number;
	viewerHeight: number;
	loadLanguage: boolean;
}

export class TextViewerDefinition<PROPERTY_TYPE extends TextViewerProperties = any,
	STATUS_TYPE extends TextViewerState = any> extends AbstractViewer<PROPERTY_TYPE, STATUS_TYPE> {
	protected loaderElement: RefObject<HTMLDivElement>;
	private resizeObserver: ResizeObserver;
	private readonly marginSize: number = 60;

	public static defaults(): HtmlViewerProperties {
		return {
			viewerMode: ViewerMode.DEFAULT,
			mouseMode: MouseMode.NONE
		} as HtmlViewerProperties;
	}

	constructor(props: Readonly<PROPERTY_TYPE>) {
		super(props);

		this.state = {
			...this.state,
			loading: true,
			reportOpen: false,
			loadedFrom: 0,
			loadedTo: 0,
			loadingOffset: 0,
			viewerHeight: 0,
			hightlighterLanguage: HighlighterLanguage.AUTO,
			loadLanguage: false
		};

		this.computeViewport = this.computeViewport.bind(this);
		this.testLineheight = this.testLineheight.bind(this);

		this.loaderElement = createRef<HTMLDivElement>();
		this.resizeObserver = new ResizeObserver(() => {
			this.computeViewport()
		});
	}

	private testLineheight(line: string, maxWidth: number): number {
		let lineHeight = 20;

		if (this.props.wrapText) {
			let tester: HTMLDivElement = document.createElement("div");
			tester.innerHTML = line;
			lineHeight = Math.ceil((tester.textContent!.length * 7) / maxWidth) * 20;
		}

		return lineHeight;
	}

	protected computeViewport(): void {
		if (!FunctionHelper.exists(this.viewerElement.current)) {
			return;
		}

		let container: HTMLDivElement = this.viewerElement.current!;
		let containerRect: DOMRect = container.getBoundingClientRect();
		let containerHeight: number = containerRect.height - DomHelper.SCROLLBAR_SIZE;
		let scrollOffset: number = container.scrollTop;

		let viewerDataValue: string = FunctionHelper.applyValue(this.state.viewerData?.value, "");
		let lines: Array<string> = FunctionHelper.applyValue(viewerDataValue.split("\n"), []);

		if (lines.length === 1) {
			// no lines found. Maybe other line break sign?
			lines = FunctionHelper.applyValue(viewerDataValue.split("\r"), []);
		}

		let viewerHeight: number = 0;
		let fromSet: boolean = false;
		let loadedFrom: number = 1;
		let toSet: boolean = false;
		let loadedTo: number = 1;
		let loadingOffset: number = 0;
		let viewportOffset: number = 0;

		for (let line of lines) {
			let itemHeight: number = this.testLineheight(
				line, containerRect.width - DomHelper.SCROLLBAR_SIZE - this.marginSize
			);

			if (!fromSet && loadingOffset + itemHeight < scrollOffset) {
				loadingOffset += itemHeight;
				loadedFrom++;
				loadedTo++;
			} else {
				fromSet = true;

				if (!toSet && viewportOffset + loadingOffset < scrollOffset + containerHeight) {
					viewportOffset += itemHeight;
					loadedTo++;
				} else {
					toSet = true;
				}
			}

			viewerHeight += itemHeight;
		}

		loadedFrom = Math.max(loadedFrom, 1);
		loadedTo = Math.min(loadedTo, lines.length);

		this.setState({
			loadingOffset: loadingOffset,
			loadedFrom: loadedFrom,
			loadedTo: loadedTo,
			viewerHeight: viewerHeight
		});
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		if (FunctionHelper.exists(this.viewerElement.current)) {
			this.resizeObserver.observe(this.viewerElement.current!);
		}

		if (FunctionHelper.exists(this.loaderElement.current)) {
			// Add active class after loader is mounted so the fade animation is applied
			DomHelper.addClass(this.loaderElement.current, "active");
		}

		try {
			await super.componentDidMount();
			await this.updateViewerData();
		} catch (e: any) {
			let error: Exception = e;

			this.setState({
				loading: false,
				error: error.message
			});
			return;
		}

		this.setState({
			loading: false,
			error: undefined
		});
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		this.setState({
			loading: true,
			error: undefined
		});

		if (FunctionHelper.exists(this.loaderElement.current)) {
			this.resizeObserver.unobserve(this.loaderElement.current!);
		}

		await super.componentWillUnmount();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<TextViewerProperties>, prevState: Readonly<TextViewerState>
	): Promise<void> {
		if (this.state.loading) {
			return;
		}

		let documentChanged: Array<boolean> = [
			this.props.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
			this.props.portalDocument.get().updated !== prevProps.portalDocument?.get().updated ||
			this.props.attachmentKey !== prevProps.attachmentKey
		];

		if (documentChanged.includes(true)) {
			this.setState({
				loading: true,
				error: undefined
			});

			try {
				await super.componentWillUnmount();
				await super.componentDidMount();
				await this.updateViewerData();
			} catch (e: any) {
				let error: Exception = e;

				this.setState({
					loading: false,
					error: error.message
				});
				return;
			}

			this.setState({
				loading: false,
				error: undefined
			});
		}

		if (this.props.wrapText !== prevProps.wrapText) {
			this.computeViewport();
		}
	}

	private generateReport(content: ReactNode): ReactNode {
		if (!FunctionHelper.exists(this.state.viewerData)) {
			return null;
		}

		let domParser: DOMParser = new DOMParser();
		let xmlTree: Document = domParser.parseFromString(
			FunctionHelper.applyValue(this.state.viewerData!.rawValue, ""), "text/xml"
		);

		let errors: Array<ReactNode> = [];
		let warnings: Array<ReactNode> = [];

		let details: HTMLCollectionOf<Element> = xmlTree.getElementsByTagName('detail');
		for (let detail of details) {
			switch (detail.getAttribute('type')) {
				case 'error':
					errors.push(
						<li key={errors.length}>{FunctionHelper.applyValue(detail.textContent, "")}</li>
					);
					break;
				case 'warning':
					warnings.push(
						<li key={warnings.length}>{FunctionHelper.applyValue(detail.textContent, "")}</li>
					);
					break;
			}
		}

		let error: HTMLCollectionOf<Element> = xmlTree.getElementsByTagName('error');
		let subError: HTMLCollectionOf<Element> = xmlTree.getElementsByTagName('sub-error');

		return (
			<div className="xml-report">
				<div className={"xml-content" + (this.state.reportOpen ? " active" : "")}>
					<div className="xml-detail-btn" onClick={() => this.setState({
						reportOpen: !this.state.reportOpen
					})}>
						<Icon icon={(this.state.reportOpen ? "icon-angle-down" : "icon-angle-right")}
							  onClick={() => this.setState({
								  reportOpen: !this.state.reportOpen
							  })}/>
						<span>
							 <Translate {...this.props} data="viewerscreen.pdfa.report" namespace="screen"/>
						</span>
					</div>
					<div className="wrapper">
						{content}
					</div>
				</div>
				{errors.length > 0 &&
                    <ul className="xml-errors">{errors}</ul>
				}
				{warnings.length > 0 &&
                    <ul className="xml-warnings">{warnings}</ul>
				}
				{(warnings.length > 0 || errors.length > 0) &&
                    <div className="xml-result">
						{error.length > 0 &&
                            <div className="xml-error">
                                <Translate {...this.props} data="viewerscreen.pdfa.error" namespace="screen"
                                           values={{
											   code: error[0].textContent
										   }}/>
                            </div>
						}
						{subError.length > 0 &&
                            <div className="xml-sub-error">
                                <Translate {...this.props} data="viewerscreen.pdfa.suberror" namespace="screen"
                                           values={{
											   code: subError[0].textContent
										   }}/>
                            </div>
						}
                    </div>
				}

				{(warnings.length === 0 || errors.length === 0) &&
                    <div className="xml-success">
                        <Translate {...this.props} data="viewerscreen.pdfa.success" namespace="screen"/>
                    </div>
				}
			</div>
		);
	}

	private async updateViewerData(): Promise<void> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let viewerData: PortalHighlightResult;
		if (this.props.viewerMode === ViewerMode.PORTFOLIO ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
			viewerData = await portalDocument.loadViewerData(attachment);
		} else {
			viewerData = await portalDocument.loadViewerData();
		}

		if (viewerSettings.hightlighterLanguage !== HighlighterLanguage.AUTO) {
			this.setState({
				loadLanguage: true
			});

			viewerData = await new Promise<PortalHighlightResult>(async (resolve, reject) => {
				try {
					let data: string = viewerData.rawValue;
					if (data.length > TextContainer.MAX_LENGHT) {
						data = data.substr(0, TextContainer.MAX_LENGHT);
					}

					let highlightWorker = new Worker(
						"worker/highlight" + portalConfiguration.cachingPath + ".js"
					);
					highlightWorker.onmessage = (event) => {
						resolve(event.data as PortalHighlightResult);
					};
					highlightWorker.postMessage({
						value: data, language: viewerSettings.hightlighterLanguage,
						cachingPath: portalConfiguration.cachingPath
					});
				} catch (exception: any) {
					reject(exception);
				}
			});
		}

		this.setState({
			viewerData: viewerData,
			loadLanguage: false
		}, () => this.computeViewport());
	}

	protected renderLines(): ReactNode {
		let viewerDataValue: string = FunctionHelper.applyValue(this.state.viewerData!.value, "");
		let lineElements: Array<ReactNode> = [];
		let lineNumber = 0;
		let lines: Array<string> = FunctionHelper.applyValue(viewerDataValue.split("\n"), []);

		if (lines.length === 1) {
			// no lines found. Maybe other line break sign?
			lines = FunctionHelper.applyValue(viewerDataValue.split("\r"), []);
		}

		for (let line of lines) {
			lineNumber++;

			if (lineNumber < this.state.loadedFrom || lineNumber > this.state.loadedTo) {
				continue;
			}

			lineElements.push(
				<li key={lineNumber}>
					<span className="line-number">{lineNumber}</span>
					<pre dangerouslySetInnerHTML={{__html: line}}/>
				</li>
			);
		}

		return (
			<code data-relevance={this.state.viewerData!.relevance}
				  className={"language " + FunctionHelper.applyValue(
					  this.state.viewerData!.language, "unknown"
				  )}>
				<div className="loading-spacer" style={this.state.viewerHeight > 0 ?
					{height: this.state.viewerHeight + LengthUnit.PIXEL} : undefined
				}/>
				<ul style={{top: this.state.loadingOffset + LengthUnit.PIXEL}}>
					{lineElements}
				</ul>
			</code>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.loading || !FunctionHelper.exists(this.state.viewerData)) {
			return (
				<div className="viewer text" ref={this.viewerElement}>
					<div className="loader" ref={this.loaderElement}>
						<span className="icon icon-spinner spin"/>
					</div>
				</div>
			);
		}

		if (FunctionHelper.exists(this.state.error)) {
			return (
				<div className="viewer text" ref={this.viewerElement}>
					{(this.props.viewerMode === ViewerMode.PREVIEW || this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW) &&
                        <div className="fallback-icon">
                            <span className="icon icon-text"/>
                        </div>
					}
					{this.props.viewerMode !== ViewerMode.PREVIEW && this.props.viewerMode !== ViewerMode.PORTFOLIO_PREVIEW &&
                        <MessageBox type={MessageType.DANGER} language={i18next.language}>
							{this.state.error}
                        </MessageBox>
					}
				</div>
			);
		}

		if (this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			return (
				<div className="fallback-icon">
					<span className="icon icon-text"/>
				</div>
			);
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let viewerDataValue: string = FunctionHelper.applyValue(this.state.viewerData!.value, "");
		let viewerClasses: string = "text-viewer";
		if (this.props.wrapText) {
			viewerClasses += " wrap";
		}

		if (MimeTypeFilter.check(portalDocument.getMimeType(), [MimeTypes.APPXML, MimeTypes.XML]) &&
			viewerDataValue.indexOf("http://schema.webpdf.de/1.0/report/pdfa") > 0) {
			return (
				<div className="viewer text" ref={this.viewerElement} onScroll={() => this.computeViewport()}>
					<div className={viewerClasses}>
						{this.generateReport(
							<>{this.renderLines()}</>
						)}
					</div>
				</div>
			);
		}

		let viewerLanguage: string = FunctionHelper.applyValue(this.state.viewerData!.language, "unknown");
		let highlighterLanguages: Array<SelectItem> = [];
		for (let language of Object.values(HighlighterLanguage)) {
			let languageLabel: string = language;
			if (language === HighlighterLanguage.AUTO &&
				viewerSettings.hightlighterLanguage === HighlighterLanguage.AUTO) {
				languageLabel += " (" + viewerLanguage + ")";
			}

			highlighterLanguages.push({label: languageLabel, value: language});
		}

		return (
			<>
				{(this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
                    <div className="viewer-toolbar toolbar in">
                        <div className="toolbar-function">
                            <div className="p-col p-grid">
                                <label className="p-col p-xs-hidden" htmlFor="control-language">
                                    <Translate {...this.props} data="viewerscreen.toolbars.language"
                                               namespace="screen"/>
                                </label>
                                <Dropdown className="language-selection" options={highlighterLanguages}
                                          value={viewerSettings.hightlighterLanguage} inputId="control-language"
                                          disabled={this.state.loadLanguage}
                                          onChange={(e) => {
											  if (this.state.loadLanguage) {
												  return;
											  }

											  viewerSettings.hightlighterLanguage = e.value;
											  this.props.context.documentManager.updateGlobalDocumentList(
												  undefined, async () => {
													  try {
														  await this.updateViewerData();
													  } catch (e: any) {
														  let error: Exception = e;

														  this.setState({
															  loading: false,
															  error: error.message
														  });
													  }
												  }
											  );
										  }}
                                          appendTo={document.getElementById("root")}/>
                            </div>
                        </div>
                    </div>
				}
				<div className="viewer text" ref={this.viewerElement} onScroll={() => this.computeViewport()}>
					<div className={viewerClasses}>
						{this.props.viewAsMarkdown &&
						(viewerSettings.hightlighterLanguage === HighlighterLanguage.MARKDOWN || (
							viewerSettings.hightlighterLanguage === HighlighterLanguage.AUTO &&
							this.state.viewerData?.language === HighlighterLanguage.MARKDOWN
						)) ?
							<ReactMarkdown linkTarget={"_blank"}>
								{FunctionHelper.applyValue(this.state.viewerData!.rawValue, "")}
							</ReactMarkdown> :
							this.renderLines()
						}
					</div>
				</div>
			</>
		);
	}
}

const TextViewer = React.memo(TextViewerDefinition,
	(prevProps: Readonly<TextViewerProperties>, nextProps: Readonly<TextViewerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument) &&
			(
				nextProps.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
				nextProps.portalDocument.get().updated !== prevProps.portalDocument?.get().updated
			)
		) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		if (nextProps.wrapText !== prevProps.wrapText) {
			return false;
		}

		if (nextProps.viewAsMarkdown !== prevProps.viewAsMarkdown) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		return true;
	}
);

export default TextViewer;